'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SubprofileType,
  createProfile,
  deleteProfile,
  getProfiles,
  getToken,
  updateProfile,
} from '@vimond/services/subprofile';
import { useContext } from 'react';
import { useAuthentication } from '../../../features/authentication';
import { SubprofileContext, SubprofileContextType } from '../provider';

type UseSubprofiles = {
  subprofiles: SubprofileType[] | undefined;
  isSubprofilesLoading: boolean;
  isSubprofilesFetched: boolean;
  subprofilesError: Error | null;
  setActiveSubprofile: (subprofile: SubprofileType) => Promise<void>;
  createSubprofile: (
    subprofile: SubprofileType,
  ) => Promise<void | SubprofileType[]>;
  deleteSubProfile: (subprofile: SubprofileType) => Promise<void | Response>;
  updateProfile: (
    subprofile: SubprofileType,
  ) => Promise<void | SubprofileType[]>;
} & SubprofileContextType;

export const useSubprofiles = (): UseSubprofiles => {
  const { jwt } = useAuthentication();

  const context = useContext(SubprofileContext);
  if (!context)
    throw new Error('useSubprofiles must be used within an SubprofileProvider');

  const { setActiveSubprofile, setSubprofileToken } = context;

  const queryClient = useQueryClient();
  const queryKey = ['subprofiles'];

  // get subprofiles
  const {
    data: subprofiles,
    error,
    isLoading: isSubprofilesLoading,
    isFetched: isSubprofilesFetched,
  } = useQuery<SubprofileType[]>({
    queryKey,
    queryFn: () => {
      if (!jwt) return Promise.resolve([]);
      return getProfiles(jwt);
    },
    enabled: !!jwt,
  });

  // create mutation for creating a subprofile
  const createSubprofileMutation = useMutation({
    mutationFn: async (newSubProfile: SubprofileType) => {
      if (!jwt) return;
      return createProfile(jwt, newSubProfile);
    },
    onSuccess: (_data, variables) => {
      // add new subprofile to cache
      const list = queryClient.getQueryData<SubprofileType[]>(queryKey) ?? [];
      const newSubProfile = variables;
      const updatedCache = [...list, newSubProfile];
      queryClient.setQueryData(queryKey, updatedCache);
    },
  });

  // create mutation for deleting a subprofile
  const deleteProfileMutation = useMutation({
    mutationFn: async (subProfile: SubprofileType) => {
      if (!jwt) return;
      return deleteProfile(jwt, subProfile);
    },
    onSuccess: (_data, variables) => {
      // remove deleted subprofile from cache
      const list = queryClient.getQueryData<SubprofileType[]>(queryKey)!;
      const deleted = variables;
      const updatedCache = list.filter(
        (profile) => profile.subProfileId !== deleted.subProfileId,
      );
      queryClient.setQueryData(queryKey, updatedCache);
    },
  });

  // create mutation for updating a subprofile
  const updateProfileMutation = useMutation({
    mutationFn: async (subProfile: SubprofileType) => {
      if (!jwt) return;
      return updateProfile(jwt, subProfile);
    },
    onSuccess(_data, variables) {
      // update subprofile in cache
      const list = queryClient.getQueryData<SubprofileType[]>(queryKey)!;
      const updated = variables;
      const updatedCache = list.map((profile) => {
        if (profile.subProfileId === updated.subProfileId) {
          return updated;
        }
        return profile;
      });
      queryClient.setQueryData(queryKey, updatedCache);
    },
  });

  const setActiveSubprofileWrapper = async (subprofile: SubprofileType) => {
    if (!jwt) return;

    // get auth token for subprofile
    const token = await getToken({ jwt, subprofile });

    // store it in session cookie so we can share it with backend
    if (context.app === 'web') {
      // get token payload
      const _payload = token.split('.')[1];
      if (!_payload) return;
       
      const payload = JSON.parse(atob(_payload));

      // get expiration date
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const expires = new Date(payload.exp * 1000);

      // take away 5 minutes from expiration date
      expires.setMinutes(expires.getMinutes() - 5);
      const cookie = JSON.stringify(subprofile);
      document.cookie = `subprofile=${cookie}; path=/; Secure; SameSite=Strict`;
      const tokenCookie = JSON.stringify(token);
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      document.cookie = `subprofile_token=${tokenCookie}; path=/; Secure; SameSite=Strict; expires=${expires}`;
    }

    setActiveSubprofile(subprofile);
    setSubprofileToken(token);

    // redirect after profile selection
    // using the redirect function from next/navigation will not work since it will not send the cookie to the backend
    if (context.app === 'web') {
      // Check for returnUrl in URL params first, then sessionStorage
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrlParam = urlParams.get('returnUrl');

      // Get stored redirect URL from sessionStorage (fallback)
      const REDIRECT_URL_KEY = 'auth_redirect_url';
      const storedUrl = sessionStorage.getItem(REDIRECT_URL_KEY);

      // Clear the stored URL
      sessionStorage.removeItem(REDIRECT_URL_KEY);

      // Use returnUrl param, then stored URL, then fallback to /browse
      let redirectUrl = '/browse';

      if (
        returnUrlParam &&
        returnUrlParam.startsWith('/') &&
        !returnUrlParam.startsWith('//')
      ) {
        redirectUrl = decodeURIComponent(returnUrlParam);
      } else if (
        storedUrl &&
        storedUrl.startsWith('/') &&
        !storedUrl.startsWith('//')
      ) {
        redirectUrl = storedUrl;
      }

      window.location.href = redirectUrl;
    }
  };

  return {
    ...context,
    setActiveSubprofile: setActiveSubprofileWrapper,
    subprofiles,
    isSubprofilesLoading,
    isSubprofilesFetched,
    subprofilesError: error,
    createSubprofile: createSubprofileMutation.mutateAsync,
    deleteSubProfile: deleteProfileMutation.mutateAsync,
    updateProfile: updateProfileMutation.mutateAsync,
  };
};
